import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Info,
  XCircle,
  BarChart2,
  Trophy,
  Clock,
  HelpCircle,
  Repeat,
  Shuffle,
  Brain,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";

import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const RuleShifter = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // Game settings
  const [gameMode, setGameMode] = useState("intro"); // intro, tutorial, play, results
  const [difficulty, setDifficulty] = useState("standard"); // easy, standard, advanced
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [gameWasCompleted, setGameWasCompleted] = useState(false);

  // Game state
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(40);
  const [currentRule, setCurrentRule] = useState(null);
  const [previousRule, setPreviousRule] = useState(null);
  const [rulesDiscovered, setRulesDiscovered] = useState(0);
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [currentCard, setCurrentCard] = useState(null);
  const [optionCards, setOptionCards] = useState([]);
  const [perseverationErrors, setPerseverationErrors] = useState(0);
  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);
  const [shiftingErrors, setShiftingErrors] = useState(0);
  const [averageResponseTime, setAverageResponseTime] = useState(0);
  const [responseStartTime, setResponseStartTime] = useState(null);
  const [responseTimes, setResponseTimes] = useState([]);

  // Game metrics
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameEndTime, setGameEndTime] = useState(null);
  const [ruleChangeCount, setRuleChangeCount] = useState(0);

  // Timer settings
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes game time
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);
  const gameActive = useRef(false);

  // Tutorial state
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialRule, setTutorialRule] = useState(null);

  // Card properties
  const shapes = ["circle", "square", "triangle", "diamond"];
  const colors = ["red", "blue", "green", "yellow"];
  const counts = [1, 2, 3, 4];
  const fills = ["solid", "outline", "striped", "dotted"];

  // Colors based on cognitive flexibility category
  const primaryColor = "from-emerald-500 to-teal-600";
  const primaryColorSolid = "bg-emerald-500";
  const secondaryColor = "text-emerald-300";
  const tertiaryColor = "bg-emerald-500/10";

  // Rules for the game
  const possibleRules = [
    { name: "color", property: "color" },
    { name: "shape", property: "shape" },
    { name: "count", property: "count" },
    { name: "fill", property: "fill" },
  ];

  // Difficulty settings
  const difficultySettings = {
    easy: {
      rulesBeforeChange: 8, // Consecutive correct answers before rule change
      properties: 3, // Number of properties that vary (easier with fewer)
      optionCount: 3, // Number of options to choose from
      responseTimeLimit: 10000, // 10 seconds to respond
      hintAvailable: true, // Hints available
    },
    standard: {
      rulesBeforeChange: 6,
      properties: 4,
      optionCount: 4,
      responseTimeLimit: 8000, // 8 seconds
      hintAvailable: false,
    },
    advanced: {
      rulesBeforeChange: 5,
      properties: 4,
      optionCount: 4,
      responseTimeLimit: 6000, // 6 seconds
      hintAvailable: false,
      noFeedback: true, // No immediate feedback on correctness in advanced mode
    },
  };

  // Fetch high scores when component mounts
  useEffect(() => {
    const fetchHighScores = async () => {
      if (!organizationId) return;
      try {
        // Reference the leaderboard document for "set-shifting"
        const leaderboardDocRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "set-shifting"
        );
        const docSnap = await getDoc(leaderboardDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Use the current difficulty as the key (converted to a string)
          const scores =
            data.leaderboards && data.leaderboards[difficulty.toString()]
              ? data.leaderboards[difficulty.toString()]
              : [];
          setHighScores(scores);
        } else {
          setHighScores([]);
        }
      } catch (error) {
        console.error("Error fetching high scores:", error);
      }
    };

    fetchHighScores();
  }, [organizationId, difficulty]);

  // Start timer when game starts
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Generate a random card with shape, color, count, and fill
  const generateRandomCard = (excludeProps = {}) => {
    // Start with all options available
    let availableShapes = [...shapes];
    let availableColors = [...colors];
    let availableCounts = [...counts];
    let availableFills = [...fills];

    // Filter based on excluded properties
    if (excludeProps.shape)
      availableShapes = availableShapes.filter((s) => s !== excludeProps.shape);
    if (excludeProps.color)
      availableColors = availableColors.filter((c) => c !== excludeProps.color);
    if (excludeProps.count)
      availableCounts = availableCounts.filter((c) => c !== excludeProps.count);
    if (excludeProps.fill)
      availableFills = availableFills.filter((f) => f !== excludeProps.fill);

    // Generate random properties
    const shape =
      availableShapes[Math.floor(Math.random() * availableShapes.length)];
    const color =
      availableColors[Math.floor(Math.random() * availableColors.length)];
    const count =
      availableCounts[Math.floor(Math.random() * availableCounts.length)];
    const fill =
      availableFills[Math.floor(Math.random() * availableFills.length)];

    return { shape, color, count, fill };
  };

  // Generate a set of option cards where exactly one matches the rule
  const generateOptions = (rule, mainCard, optionCount) => {
    // Create the correct option first - matching the rule
    const correctOption = { ...generateRandomCard() };
    correctOption[rule.property] = mainCard[rule.property]; // Make sure it matches the rule

    const options = [correctOption];

    // Create remaining options that don't match the rule
    while (options.length < optionCount) {
      // Generate a random card
      const newOption = generateRandomCard();

      // Ensure it doesn't match the rule
      if (newOption[rule.property] !== mainCard[rule.property]) {
        // Check if this option is unique
        const isDuplicate = options.some(
          (opt) =>
            opt.shape === newOption.shape &&
            opt.color === newOption.color &&
            opt.count === newOption.count &&
            opt.fill === newOption.fill
        );

        if (!isDuplicate) {
          options.push(newOption);
        }
      }
    }

    // Shuffle the options
    return options.sort(() => Math.random() - 0.5);
  };

  // Select a new rule distinct from the previous one
  const selectNewRule = () => {
    let availableRules = [...possibleRules];

    // Filter out the previous rule
    if (previousRule) {
      availableRules = availableRules.filter(
        (r) => r.property !== previousRule.property
      );
    }

    // Randomly select from available rules
    const newRule =
      availableRules[Math.floor(Math.random() * availableRules.length)];

    return newRule;
  };

  // Start a new game
  const startGame = () => {
    console.log("Starting game...");

    // Reset all game state
    setScore(0);
    setCurrentRound(0);
    setConsecutiveCorrect(0);
    setRulesDiscovered(0);
    setPerseverationErrors(0);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setShiftingErrors(0);
    setResponseTimes([]);
    setAverageResponseTime(0);
    setRuleChangeCount(0);

    // Set playing state
    setIsPlaying(true);
    gameActive.current = true;

    // Set time based on difficulty
    setTimeLeft(difficulty === "easy" ? 480 : 600); // 8 minutes for easy, 10 for others

    setGameStartTime(Date.now());
    setGameEndTime(null);

    // Select first rule
    const firstRule =
      possibleRules[Math.floor(Math.random() * possibleRules.length)];
    setCurrentRule(firstRule);
    setPreviousRule(null);

    // Start first round
    startNewRound(firstRule);
  };

  const endGame = () => {
    console.log("Ending game...");

    // Clear any pending intervals
    clearInterval(timerRef.current);

    // Set game state to not playing
    setIsPlaying(false);
    gameActive.current = false;

    // Record end time
    setGameEndTime(Date.now());

    // Calculate final metrics
    if (responseTimes.length > 0) {
      const avg =
        responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
      setAverageResponseTime(Math.round(avg));
    }

    // Change game mode to results
    // Only consider it "completed" if user played to the end
    const completedByRounds = currentRound >= totalRounds;
    const completedByTimer = timeLeft === 0;
    setGameWasCompleted(completedByRounds || completedByTimer);
    setGameMode("results");
  };

  // Start a new round
  const startNewRound = (rule = currentRule) => {
    if (!gameActive.current || currentRound >= totalRounds) {
      endGame();
      return;
    }

    // Generate a main card
    const mainCard = generateRandomCard();
    setCurrentCard(mainCard);

    // Get number of options based on difficulty
    const optionCount = difficultySettings[difficulty].optionCount;

    // Generate options where one matches the current rule
    const options = generateOptions(rule, mainCard, optionCount);
    setOptionCards(options);

    // Set response start time
    setResponseStartTime(Date.now());

    setCurrentRound((prev) => prev + 1);
  };

  // Check if it's time to change the rule
  const checkRuleChange = () => {
    const threshold = difficultySettings[difficulty].rulesBeforeChange;

    if (consecutiveCorrect >= threshold) {
      // Change the rule
      const oldRule = currentRule;
      const newRule = selectNewRule();

      setConsecutiveCorrect(0);
      setCurrentRule(newRule);
      setPreviousRule(oldRule);
      setRuleChangeCount((prev) => prev + 1);

      // Add rule discovery points
      setScore((prev) => prev + 50);
      setRulesDiscovered((prev) => prev + 1);

      return true;
    }

    return false;
  };

  // Handle option selection
  const handleOptionSelect = (option, index) => {
    if (!isPlaying) return;

    // Calculate response time
    const responseTime = Date.now() - responseStartTime;
    setResponseTimes((prev) => [...prev, responseTime]);

    // Check if the selected option matches the rule
    const isCorrect =
      option[currentRule.property] === currentCard[currentRule.property];

    if (isCorrect) {
      // Correct answer
      setConsecutiveCorrect((prev) => prev + 1);
      setCorrectResponses((prev) => prev + 1);

      // Calculate score based on response time
      let roundScore;
      if (responseTime < 1000) {
        roundScore = 30; // Very fast
      } else if (responseTime < 2000) {
        roundScore = 25; // Fast
      } else if (responseTime < 3000) {
        roundScore = 20; // Good
      } else if (responseTime < 4000) {
        roundScore = 15; // Average
      } else {
        roundScore = 10; // Slow
      }

      setScore((prev) => prev + roundScore);
      setFeedbackCorrect(true);
      setFeedbackMessage("Correct!");

      // Check if it's time to change the rule
      const ruleChanged = checkRuleChange();

      if (ruleChanged) {
        setFeedbackMessage("Rule Changed!");
      }
    } else {
      // Incorrect answer
      setConsecutiveCorrect(0);
      setIncorrectResponses((prev) => prev + 1);

      // Check if this is a perseveration error (using the old rule)
      if (
        previousRule &&
        option[previousRule.property] === currentCard[previousRule.property]
      ) {
        setPerseverationErrors((prev) => prev + 1);
        setShiftingErrors((prev) => prev + 1);
      }

      // Subtract points for incorrect answer
      setScore((prev) => Math.max(0, prev - 10));
      setFeedbackCorrect(false);
      setFeedbackMessage("Incorrect");
    }

    // Show feedback unless in advanced mode with no feedback
    if (
      !(difficulty === "advanced" && difficultySettings[difficulty].noFeedback)
    ) {
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 800);
    }

    // Start next round or end game
    if (currentRound < totalRounds) {
      setTimeout(
        () => {
          startNewRound();
        },
        difficulty === "advanced" ? 400 : 800
      );
    } else {
      setTimeout(() => {
        endGame();
      }, 800);
    }
  };

  // Handle the tutorial steps
  const advanceTutorial = () => {
    if (tutorialStep < 4) {
      setTutorialStep((prev) => prev + 1);

      // If moving to step 1, set up a tutorial example
      if (tutorialStep === 0) {
        // Setup a simple demo with a color rule
        setTutorialRule({ name: "color", property: "color" });
      }
    } else {
      // End tutorial and start the game
      setGameMode("play");
      startGame();
    }
  };

  // Save score to Firebase
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // 🧠 Resolve userName with fallbacks
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // 🧱 Unified gameData object
      const gameData = {
        gameType: "set-shifting",
        gameId: "set-shifting",
        mode: "single",
        userId: user.email,
        userName,
        score,
        difficulty,
        correctResponses,
        incorrectResponses,
        perseverationErrors,
        shiftingErrors,
        ruleChangeCount,
        rulesDiscovered,
        averageResponseTime,
        gameLength: gameEndTime - gameStartTime,
        timestamp: new Date(),
      };

      // 🔗 Firebase references
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "set-shifting"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // 🧠 Custom sorting logic: score > ruleChangeCount > avgRT
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        if (a.ruleChangeCount !== b.ruleChangeCount)
          return b.ruleChangeCount - a.ruleChangeCount;
        if (a.averageResponseTime !== b.averageResponseTime)
          return a.averageResponseTime - b.averageResponseTime;
        return a.timestamp - b.timestamp;
      };

      // 🔁 Transaction for leaderboard + user score
      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const levelKey = difficulty.toString();
        let currentLeaderboard = leaderboardData.leaderboards[levelKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[levelKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // 💾 Save to gameHistory
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );

      await setDoc(gameHistoryRef, gameData);

      // 🔄 Reset game state
      setGameMode("intro");
      setScore(0);
    } catch (error) {
      console.error("Error saving score:", error);
    }
  };

  // Render a card with the given properties
  const renderCard = (card, small = false) => {
    if (!card) return null;

    const shapes = [];
    for (let i = 0; i < card.count; i++) {
      shapes.push(renderShape(card, i, small));
    }

    return (
      <div
        className={`${
          small ? "w-32 h-32" : "w-48 h-48"
        } bg-slate-800 rounded-lg flex items-center justify-center p-4`}
      >
        <div className="flex flex-wrap justify-center items-center gap-2">
          {shapes}
        </div>
      </div>
    );
  };

  // Render a shape within a card
  const renderShape = (card, key, small = false) => {
    const { shape, color, fill } = card;
    const size = small ? "w-10 h-10" : "w-16 h-16";
    const colorClasses = {
      red: fill === "solid" ? "bg-red-500" : "border-red-500 text-red-500",
      blue: fill === "solid" ? "bg-blue-500" : "border-blue-500 text-blue-500",
      green:
        fill === "solid" ? "bg-green-500" : "border-green-500 text-green-500",
      yellow:
        fill === "solid"
          ? "bg-yellow-500"
          : "border-yellow-500 text-yellow-500",
    };

    const shapeClasses = {
      circle: "rounded-full",
      square: "rounded-none",
      triangle: "triangle",
      diamond: "diamond",
    };

    const fillClasses = {
      solid: "border-0",
      outline: "border-2 bg-transparent",
      striped: "striped",
      dotted: "dotted",
    };

    return (
      <div
        key={key}
        className={`${size} ${shapeClasses[shape]} ${colorClasses[color]} ${fillClasses[fill]} border-2 flex items-center justify-center`}
      >
        {fill === "dotted" && (
          <div className="dot-pattern absolute inset-0"></div>
        )}
        {fill === "striped" && (
          <div className="stripe-pattern absolute inset-0"></div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-emerald-300">
              Rule Shifter
            </h1>
            <div className="p-2 bg-emerald-500/10 rounded-full">
              <Repeat className="text-emerald-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/10 rounded-lg">
                      <Repeat className="text-emerald-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      How Rule Shifter Works
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <h3 className="text-emerald-300 font-medium mb-2">
                      Game Overview
                    </h3>
                    <p className="text-slate-300">
                      Rule Shifter measures and improves your cognitive
                      flexibility – the brain's ability to adapt to changing
                      rules and situations. You'll discover hidden rules, use
                      them, and then switch strategies when the rules change.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-emerald-300 font-medium mb-2">
                      How to Play
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-slate-300">
                      <li>
                        You'll be shown a main card with various properties
                        (color, shape, number, pattern)
                      </li>
                      <li>
                        Choose from the options below that you think matches the
                        hidden rule
                      </li>
                      <li>
                        The hidden rule is based on a single property (e.g.,
                        "match the color")
                      </li>
                      <li>
                        After several correct answers, the rule will change
                        without warning
                      </li>
                      <li>
                        You must discover the new rule and adapt your strategy
                      </li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="text-emerald-300 font-medium mb-2">
                      Difficulty Levels
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>
                        <span className="font-medium">Easy:</span> Fewer
                        properties to track, slower pace, with hints
                      </li>
                      <li>
                        <span className="font-medium">Standard:</span> All
                        properties in play, moderate pace
                      </li>
                      <li>
                        <span className="font-medium">Advanced:</span> Faster
                        pace, no feedback on correct/incorrect
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-emerald-300 font-medium mb-2">
                      Recovery Benefits
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>
                        Improves mental flexibility often impaired by substance
                        use
                      </li>
                      <li>
                        Reduces cognitive rigidity and black-and-white thinking
                      </li>
                      <li>
                        Strengthens ability to adapt to new situations and
                        challenges
                      </li>
                      <li>Enhances problem-solving in changing environments</li>
                    </ul>
                  </div>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-3 rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Game Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Repeat size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Rule Shifter
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Discover hidden patterns and adapt quickly as rules change.
                  Strengthen your cognitive flexibility and reduce mental
                  rigidity.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-emerald-500/10 rounded-full mb-2">
                    <Brain size={24} className="text-emerald-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Cognitive Flexibility
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Improves ability to adjust to changing rules and situations
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-emerald-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-emerald-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">8-12 minutes</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Sessions to build adaptive thinking
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-emerald-500/10 rounded-full mb-2">
                    <Shuffle size={24} className="text-emerald-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Pattern Recognition
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Learn to identify and adapt to changing patterns
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty Level
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {["easy", "standard", "advanced"].map((level) => (
                      <button
                        key={level}
                        onClick={() => setDifficulty(level)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors capitalize ${
                          difficulty === level
                            ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => {
                      setGameMode("play");
                      startGame();
                      startGame();
                    }}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* High Scores */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Level
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Rules Discovered
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Response Time
                          </th>
                          <th className="py-2 px-4 text-right text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((score, index) => {
                          // Format the name to show first name and first letter of last name
                          let formattedName = score.userName;

                          if (score.userName && score.userName.includes(" ")) {
                            const nameParts = score.userName.split(" ");
                            const firstName = nameParts[0];
                            const lastInitial = nameParts[1]
                              ? nameParts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }

                          return (
                            <tr
                              key={score.id}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-slate-300 capitalize">
                                {score.difficulty}
                              </td>
                              <td className="py-2 px-4 text-center text-emerald-300">
                                {score.rulesDiscovered || 0}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {score.averageResponseTime
                                  ? `${score.averageResponseTime} ms`
                                  : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-right text-emerald-300 font-medium">
                                {score.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Rule Shifter
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  {tutorialStep === 0 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Rule Shifter challenges your cognitive flexibility – the
                        ability to adapt your thinking as rules change.
                      </p>
                      <p className="text-slate-300">
                        In this game, you'll be shown a card with various
                        properties. Your task is to figure out the hidden rule
                        by selecting matching cards.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 1 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Let's look at an example. Here's a main card with
                        different properties:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-slate-300">
                        <li>
                          <span className="text-emerald-300">Color:</span> Blue
                        </li>
                        <li>
                          <span className="text-emerald-300">Shape:</span>{" "}
                          Circle
                        </li>
                        <li>
                          <span className="text-emerald-300">Count:</span> 2
                        </li>
                        <li>
                          <span className="text-emerald-300">Fill:</span> Solid
                        </li>
                      </ul>
                      <p className="text-slate-300">
                        The hidden rule could be any one of these properties.
                        Let's say the rule is "match the color".
                      </p>
                    </div>
                  )}

                  {tutorialStep === 2 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        When you select the correct option (the one that matches
                        the color), you'll get feedback that you're correct.
                      </p>
                      <p className="text-slate-300">
                        After several correct answers (the number depends on
                        difficulty), the rule will change without warning!
                      </p>
                      <p className="text-slate-300">
                        For example, the new rule might become "match the shape"
                        instead of "match the color".
                      </p>
                    </div>
                  )}

                  {tutorialStep === 3 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Here's where cognitive flexibility comes in. You'll need
                        to:
                      </p>
                      <ol className="list-decimal list-inside space-y-2 text-slate-300">
                        <li>
                          Notice that your previous strategy isn't working
                          anymore
                        </li>
                        <li>
                          Test new hypotheses about what the rule might be
                        </li>
                        <li>Switch your approach based on feedback</li>
                      </ol>
                      <p className="text-slate-300">
                        This is called "set-shifting" – the ability to shift
                        your mental set or approach when circumstances change.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 4 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        You're ready to play! Here's what to remember:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-slate-300">
                        <li>
                          Find the hidden rule by testing different options
                        </li>
                        <li>
                          After several correct matches, the rule will change
                        </li>
                        <li>
                          Adapt quickly when you notice your strategy isn't
                          working
                        </li>
                        <li>
                          The faster you respond correctly, the higher your
                          score
                        </li>
                      </ul>
                      <p className="text-slate-300">
                        This exercise strengthens neural pathways impaired by
                        substance use and improves your ability to adapt to
                        life's changes.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tutorial visualization */}
              <div className="flex flex-col md:flex-row gap-6 items-center">
                {tutorialStep >= 1 && (
                  <div className="flex-1 flex flex-col items-center">
                    <h3 className="text-white font-medium mb-4">Main Card</h3>
                    <div className="w-32 h-32 bg-slate-800 rounded-lg flex items-center justify-center p-2">
                      <div className="flex flex-wrap justify-center items-center gap-1">
                        {[0, 1].map((i) => (
                          <div
                            key={i}
                            className="w-10 h-10 rounded-full bg-blue-500 border-2 border-blue-500"
                          ></div>
                        ))}
                      </div>
                    </div>

                    {tutorialStep >= 2 && (
                      <div className="mt-6">
                        <h3 className="text-white font-medium mb-4">Options</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {/* Option 1 - correct (matches blue color) */}
                          <div className="w-24 h-24 bg-slate-800 rounded-lg flex items-center justify-center p-2 border-2 border-green-500">
                            <div className="w-10 h-10 square bg-blue-500 border-2 border-blue-500"></div>
                          </div>

                          {/* Option 2 - incorrect (wrong color) */}
                          <div className="w-24 h-24 bg-slate-800 rounded-lg flex items-center justify-center p-2">
                            <div className="w-10 h-10 rounded-full bg-red-500 border-2 border-red-500"></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex-1 space-y-4">
                  {tutorialStep === 1 && (
                    <div className="bg-slate-700/30 p-4 rounded-lg">
                      <h3 className="text-white font-medium mb-2">
                        The Hidden Rule
                      </h3>
                      <p className="text-emerald-300 font-medium">
                        "Match the color" - In this example, you would need to
                        select cards that are blue.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 2 && (
                    <div className="bg-slate-700/30 p-4 rounded-lg">
                      <h3 className="text-white font-medium mb-2">
                        Correct Answer
                      </h3>
                      <p className="text-slate-300">
                        The left option is correct because it matches the{" "}
                        <span className="text-blue-400">blue color</span>,
                        following our rule.
                      </p>
                      <p className="text-green-400 mt-2">
                        After several correct answers, the rule will change!
                      </p>
                    </div>
                  )}

                  {/* Navigation buttons */}
                  <div className="flex justify-between mt-8">
                    {tutorialStep > 0 ? (
                      <button
                        onClick={() => setTutorialStep((prev) => prev - 1)}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Previous
                      </button>
                    ) : (
                      <button
                        onClick={() => setGameMode("intro")}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Back
                      </button>
                    )}

                    <button
                      onClick={advanceTutorial}
                      className="py-2 px-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg transition-colors"
                    >
                      {tutorialStep < 4 ? "Next" : "Start Game"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Game Play Screen */}
          {gameMode === "play" && (
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-emerald-500/20 to-teal-600/20 px-4 py-2 rounded-lg">
                  <span className="text-emerald-300 font-medium capitalize">
                    Level: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>

                  <div className="ml-4 bg-gradient-to-r from-emerald-500/20 to-teal-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center">
                {/* Rules Discovered Indicator */}
                <div className="mb-6 w-full max-w-xl">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-slate-400 text-sm">
                      Rules Discovered
                    </span>
                    <span className="text-white font-medium">
                      {rulesDiscovered}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-600"
                      style={{
                        width: `${Math.min(
                          (rulesDiscovered / 10) * 100,
                          100
                        )}%`,
                      }}
                    ></div>
                  </div>
                </div>

                {/* Main card area */}
                <div className="mb-8">
                  <h3 className="text-white font-medium mb-4 text-center">
                    Find the matching rule
                  </h3>
                  {currentCard && renderCard(currentCard)}
                </div>

                {/* Options area */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                  {optionCards.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleOptionSelect(option, index)}
                      className="bg-transparent transition-all transform hover:scale-105 focus:outline-none"
                      disabled={!isPlaying || showFeedback}
                    >
                      {renderCard(option, true)}
                    </button>
                  ))}
                </div>

                {/* Feedback overlay */}
                <AnimatePresence>
                  {showFeedback && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.2 }}
                      className="fixed inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm z-10"
                    >
                      <div className="text-center">
                        <div
                          className={`text-3xl font-bold ${
                            feedbackCorrect ? "text-green-400" : "text-red-400"
                          }`}
                        >
                          {feedbackMessage}
                        </div>
                        {feedbackMessage === "Rule Changed!" && (
                          <p className="text-emerald-300 mt-2">
                            Discover the new rule!
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Game stats */}
              <div className="mt-8 max-w-2xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Streak</div>
                  <div className="text-white font-medium">
                    {consecutiveCorrect}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Round</div>
                  <div className="text-white font-medium">
                    {currentRound}/{totalRounds}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Correct</div>
                  <div className="text-green-400 font-medium">
                    {correctResponses}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Errors</div>
                  <div className="text-red-400 font-medium">
                    {incorrectResponses}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to end the game early?"
                      )
                    ) {
                      endGame();
                    }
                  }}
                  className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                >
                  End Game
                </button>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You've completed the Rule Shifter challenge on {difficulty}{" "}
                  difficulty.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-emerald-400" />
                    Your Results
                  </h3>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-emerald-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium capitalize">
                        {difficulty}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Rules Discovered</span>
                      <span className="text-emerald-300 font-medium">
                        {rulesDiscovered}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct Responses</span>
                      <span className="text-green-400">{correctResponses}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">
                        Incorrect Responses
                      </span>
                      <span className="text-red-400">{incorrectResponses}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">
                        Perseveration Errors
                      </span>
                      <span className="text-orange-400">
                        {perseverationErrors}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Response Time</span>
                      <span className="text-white">
                        {averageResponseTime > 0
                          ? `${averageResponseTime} ms`
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-emerald-400" />
                    Performance Analysis
                  </h3>

                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {correctResponses + incorrectResponses > 0
                            ? `${Math.round(
                                (correctResponses /
                                  (correctResponses + incorrectResponses)) *
                                  100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-teal-600"
                          style={{
                            width:
                              correctResponses + incorrectResponses > 0
                                ? `${
                                    (correctResponses /
                                      (correctResponses + incorrectResponses)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Cognitive Flexibility Rating
                      </h4>
                      <div className="bg-slate-700/30 p-3 rounded-lg text-sm">
                        {perseverationErrors <= 3 && rulesDiscovered >= 5 && (
                          <p className="text-green-400">
                            Excellent: You adapt quickly to changing rules and
                            show strong cognitive flexibility!
                          </p>
                        )}
                        {perseverationErrors <= 5 &&
                          perseverationErrors > 3 &&
                          rulesDiscovered >= 3 && (
                            <p className="text-green-300">
                              Good: You show above-average ability to shift
                              between different rules.
                            </p>
                          )}
                        {((perseverationErrors <= 8 &&
                          perseverationErrors > 5) ||
                          (rulesDiscovered >= 2 && rulesDiscovered < 3)) && (
                          <p className="text-yellow-300">
                            Developing: You're building cognitive flexibility
                            with room to grow.
                          </p>
                        )}
                        {perseverationErrors > 8 ||
                          (rulesDiscovered < 2 && (
                            <p className="text-orange-300">
                              Emerging: Focus on recognizing when rules change
                              and letting go of old patterns.
                            </p>
                          ))}

                        <p className="mt-2 text-slate-300">
                          Shifting errors: {shiftingErrors} | Rule changes:{" "}
                          {ruleChangeCount}
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Cognitive Benefits
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-emerald-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-emerald-400" />
                          </div>
                          <span className="text-slate-300">
                            Strengthens prefrontal cortex function for better
                            adaptability
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-emerald-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-emerald-400" />
                          </div>
                          <span className="text-slate-300">
                            Reduces cognitive rigidity often seen during
                            recovery
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-emerald-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-emerald-400" />
                          </div>
                          <span className="text-slate-300">
                            Improves ability to adapt to changing life
                            circumstances
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameWasCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}

                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS for special shapes and fills */}
      <style>
        {`
    .triangle {
      clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
    }
    
    .diamond {
      clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
    }
    
    .striped {
      background-image: repeating-linear-gradient(
        45deg,
        currentColor,
        currentColor 2px,
        transparent 2px,
        transparent 8px
      );
    }
    
    .dotted {
      background-image: radial-gradient(
        currentColor 15%,
        transparent 15%
      );
      background-size: 8px 8px;
    }
  `}
      </style>
    </div>
  );
};

export default RuleShifter;
